import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { Button, Col, Row } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';

import TimeFilter from '../Common/TimeFilter';
import ToggleSwitch from '../Common/ToggleSwitch';
import {
  appointmentRequestPageTypes,
  appointmentRequestStatus,
  DEFAULT_PAGINATION_LENGTH,
} from '../../../config/app.config';
import { formatDate } from '../../../helpers/formatDate';

import { toggleAppointmentRequestsModalClass } from '../ParentProfile/utils/toggleAppointmentRequestsModalClass';
import PaginationComponent from '../../../shared/components/pagination/Pagination';
import MessageModal from '../../../shared/components/Modals/MessageModal';
import HeadingText from '../../../shared/components/form/HeadingText';
import StandardTableToolbar from '../../../shared/components/table-toolbar/StandardTableToolbar';
import withRouter from '../../../shared/components/withRouter';

import { toggleLoading } from '../../../redux/actions/commonActions';
import {
  getAllAppointmentRequests,
  resetAppointmentRequests,
} from '../../../redux/actions/appointmentRequestActions';
import { setPetAppointmentRequestsNumber } from '../../../redux/actions/petActions';

import {
  AppointmentRequestsTable,
  appointmentComponentsWrapperMap,
} from './Components';
import { AppointmentRequestModal } from './RequestDetails';
import {
  AppointmentRequestModalProvider,
  SuccessModalProvider,
  useAppointmentRequestsTableHeaders,
} from './utils';
import { AppointmentRequestMessageModal } from '../../modals/AppointmentRequestMessage';
import { useGetAppointmentTypes } from '../../App/customize/hooks/useGetAppointmentTypes';
import { useAppointmentLabels } from '../../App/customize/hooks/useAppointmentLabels';
import {
  APPOINTMENT_REQUEST_ALL_STATUSES,
  APPOINTMENT_REQUEST_DATE_SHORTCUTS,
  APPOINTMENT_REQUEST_FILTER_SEARCH_THRESHOLD,
  APPOINTMENT_REQUEST_OPEN_STATUSES,
  APPOINTMENT_REQUEST_SCOPE_OPTIONS,
  APPOINTMENT_REQUEST_SORT_OPTIONS,
  getAppointmentRequestFilterCategories,
} from './utils/appointmentRequestToolbarConfig';

const { pending, completed } = appointmentRequestStatus;

const initialParams = {
  search: '',
  timeFilter: null,
  sortOrder: -1,
  sortField: 'createdAt',
  offset: 0,
  limit: DEFAULT_PAGINATION_LENGTH,
  status: [pending],
  filters: {},
  range: {},
  scope: 'open',
};

function reducer(params, action) {
  switch (action.type) {
    case 'SET_SEARCH':
      return { ...params, search: action.payload, offset: 0 };
    case 'SET_TIME_FILTER':
      return {
        ...params,
        timeFilter: action.payload,
        offset: action.payload === 'range' ? params.offset : 0,
      };
    case 'SET_SORT': {
      const { field, order } = action.payload;
      return {
        ...params,
        sortField: field ?? params.sortField,
        sortOrder: order ?? params.sortOrder,
        offset: 0,
      };
    }
    case 'SET_FILTER':
      return {
        ...params,
        filters: {
          ...params.filters,
          [action.payload.type]: action.payload.filters,
        },
        offset: 0,
      };
    case 'SET_SCOPE':
      return {
        ...params,
        scope: action.payload,
        offset: 0,
      };
    case 'TOGGLE_STATUS': {
      const newStatus = action.payload;
      return { ...params, status: newStatus, offset: 0 };
    }
    case 'SET_RANGE':
      return { ...params, range: action.payload, offset: 0 };
    case 'SET_LIMIT':
      return { ...params, limit: action.payload, offset: 0 };
    case 'SET_OFFSET':
      return { ...params, offset: action.payload };
    case 'CLEAR_ALL_FILTERS':
      return {
        ...params,
        filters: {},
        offset: 0,
      };
    default:
      return params;
  }
}

function AppointmentRequests({
  type = appointmentRequestPageTypes.default,
  petId,
  parentId,
  tableHeaderPosition = '60px',
}) {
  const dispatch = useDispatch();
  const isDefaultPage = type === appointmentRequestPageTypes.default;
  const labels = useAppointmentLabels();

  const { appointmentRequestsList } = useSelector(
    (state) => state.appointmentRequests,
  );
  const doctors = useSelector(
    (state) => state.staff.staffDoctorsByFacility || [],
  );

  const { data: appointmentTypes = [] } = useGetAppointmentTypes({
    filter: { used: 1 },
  });

  const { isPetAppointmentRequestsModalOpen, petsAppointmentRequestsNumber } =
    useSelector((state) => state.pets);

  const headers = useAppointmentRequestsTableHeaders(type);

  const [params, localDispatch] = useReducer(reducer, initialParams);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showUpdatedHint, setShowUpdatedHint] = useState(false);
  const updatedHintTimerRef = useRef(null);

  const isRequestStatusFilterExist =
    params.filters.requestStatus && params.filters.requestStatus.length > 0;

  const hasAdditionalNarrowing =
    !!params.search?.trim() ||
    !!params.timeFilter ||
    Object.values(params.filters || {}).some(
      (value) => Array.isArray(value) && value.length > 0,
    );
  const emptyStateMessage = hasAdditionalNarrowing
    ? 'No matching Appointment Requests to display'
    : 'No Appointment Requests to display';

  const filterCategories = useMemo(
    () =>
      getAppointmentRequestFilterCategories({
        appointmentTypes,
        doctors,
        appointmentTypeLabel: labels.appointmentType,
      }),
    [appointmentTypes, doctors, labels.appointmentType],
  );

  const getAppointmentRequests = useCallback(async () => {
    const formattedStartDate = formatDate(params.range.startDate);
    const formattedEndDate = formatDate(params.range.endDate);

    const { typeTitle, doctorId, requestStatus } = params.filters;

    const resolvedStatus = isDefaultPage
      ? requestStatus?.length > 0
        ? requestStatus
        : params.scope === 'open'
          ? APPOINTMENT_REQUEST_OPEN_STATUSES
          : APPOINTMENT_REQUEST_ALL_STATUSES
      : params.status?.length > 0
        ? params.status
        : null;

    await dispatch(
      getAllAppointmentRequests({
        sort: {
          field: params.sortField,
          order: +params.sortOrder,
        },
        search: params.search,
        doctorId: doctorId?.length > 0 ? doctorId : null,
        type: typeTitle?.length > 0 ? typeTitle : null,
        status: resolvedStatus,
        parentId,
        petId,
        timeFilter: {
          type: params.timeFilter,
          startDate: formattedStartDate,
          endDate: formattedEndDate,
        },
        limit: params.limit,
        offset: params.offset,
      }),
    );
  }, [params, parentId, petId, dispatch, isDefaultPage]);

  const handleRefetchAppointmentRequests = useCallback(
    async (withLoading = true) => {
      if (withLoading) {
        dispatch(toggleLoading(true));
      }
      await getAppointmentRequests();
      if (withLoading) {
        dispatch(toggleLoading(false));
      }

      if (isDefaultPage && withLoading) {
        setShowUpdatedHint(true);
        if (updatedHintTimerRef.current) {
          clearTimeout(updatedHintTimerRef.current);
        }
        updatedHintTimerRef.current = setTimeout(() => {
          updatedHintTimerRef.current = null;
          setShowUpdatedHint(false);
        }, 1500);
      }
    },
    [getAppointmentRequests, dispatch, isDefaultPage],
  );

  useEffect(() => {
    const fetchAppointmentRequests = async () => {
      dispatch(toggleLoading(true));
      await getAppointmentRequests();
      dispatch(toggleLoading(false));
    };

    fetchAppointmentRequests();
  }, [getAppointmentRequests, dispatch]);

  useEffect(() => {
    setRows(appointmentRequestsList?.data || []);
    setTotal(appointmentRequestsList?.total || 0);

    if (type === appointmentRequestPageTypes.clientPageModal) {
      dispatch(
        setPetAppointmentRequestsNumber({
          [petId]: appointmentRequestsList?.total ?? 0,
        }),
      );
    }
  }, [appointmentRequestsList, type, petId, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetAppointmentRequests());
      if (updatedHintTimerRef.current) {
        clearTimeout(updatedHintTimerRef.current);
      }
    };
  }, [dispatch]);

  const handleTimeFilterChange = (timeFilter) => {
    localDispatch({ type: 'SET_TIME_FILTER', payload: timeFilter });
  };

  const handleSort = ({ field, order }) => {
    localDispatch({ type: 'SET_SORT', payload: { field, order } });
  };

  const handleFilter = (filterType, filters) => {
    localDispatch({ type: 'SET_FILTER', payload: { type: filterType, filters } });
  };

  const handleScopeChange = (scope) => {
    localDispatch({ type: 'SET_SCOPE', payload: scope });
  };

  const handleStatusToggle = (addedStatus) => {
    const newStatus = params.status.includes(addedStatus)
      ? params.status.filter((s) => s !== addedStatus)
      : [...params.status, addedStatus];

    localDispatch({ type: 'TOGGLE_STATUS', payload: newStatus });
  };

  const handleDateRangeChange = (range) => {
    localDispatch({ type: 'SET_RANGE', payload: range });
  };

  const handlePageLimit = (newLimit) => {
    localDispatch({ type: 'SET_LIMIT', payload: newLimit });
  };

  const handleSearchChange = (search) => {
    localDispatch({ type: 'SET_SEARCH', payload: search });
  };

  const handleClearAllFilters = () => {
    localDispatch({ type: 'CLEAR_ALL_FILTERS' });
  };

  const handleToggleSuccessModal = async (modalType) => {
    switch (modalType) {
      case 'open': {
        setShowSuccessModal(true);

        await getAppointmentRequests();
        break;
      }
      case 'close': {
        setShowSuccessModal(false);

        if (
          petsAppointmentRequestsNumber &&
          isPetAppointmentRequestsModalOpen
        ) {
          toggleAppointmentRequestsModalClass({
            action: 'remove',
          });
        }

        break;
      }
      default: {
        break;
      }
    }
  };

  const Wrapper = appointmentComponentsWrapperMap[type];

  const ToggleSwitchesColumn = () => (
    <Col className="toggle-switches-column">
      <div className="toggle-switches-block">
        <ToggleSwitch
          label="Pending Requests"
          checked={params.status.includes(pending)}
          handleToggle={() => handleStatusToggle(pending)}
        />
        <ToggleSwitch
          label="Completed Requests"
          checked={params.status.includes(completed)}
          handleToggle={() => handleStatusToggle(completed)}
        />
      </div>
    </Col>
  );

  return (
    <AppointmentRequestModalProvider
      handleRefetchAppointmentRequests={handleRefetchAppointmentRequests}
    >
      <SuccessModalProvider handleToggleSuccessModal={handleToggleSuccessModal}>
        <Col
          md={12}
          lg={12}
          className="manage-patient-container admin-container"
        >
          <Wrapper>
{isDefaultPage ? (
  <div className="sticky-table">
    <div className="ptb-board-header">
      <div className="page-title-with-action ptb-board-header__title-row">
        <HeadingText text="Appointment Requests" />
        <Button
          type="button"
          className="btn-utility"
          onClick={() => handleRefetchAppointmentRequests(true)}
        >
          Refresh
        </Button>
        {showUpdatedHint ? (
          <span style={{ fontSize: '12px', color: '#999' }}>
            Updated!
          </span>
        ) : null}
      </div>
      <div className="ptb-board-header__subtitle-row">
        <div className="ptb-board-header__subtitle-stack">
          <div className="ptb-board-header__subtitle">
            Appointment requests submitted
          </div>
          <div className="ptb-board-header__subtitle">
            by Pet Parents through the Loyal Pet mobile app
          </div>
        </div>
      </div>
    </div>
    <div className="refill-requests-toolbar">
      <StandardTableToolbar
        searchValue={params.search}
        onSearchChange={handleSearchChange}
        scope={isRequestStatusFilterExist ? null : params.scope}
        onScopeChange={handleScopeChange}
        scopeOptions={APPOINTMENT_REQUEST_SCOPE_OPTIONS}
        sortField={params.sortField}
        sortOrder={params.sortOrder}
        onSortChange={handleSort}
        sortOptions={APPOINTMENT_REQUEST_SORT_OPTIONS}
        filters={params.filters}
        onFilterChange={handleFilter}
        onClearAllFilters={handleClearAllFilters}
        filterCategories={filterCategories}
        filterSearchThreshold={APPOINTMENT_REQUEST_FILTER_SEARCH_THRESHOLD}
        timeFilter={params.timeFilter}
        onTimeFilterChange={handleTimeFilterChange}
        dateShortcuts={APPOINTMENT_REQUEST_DATE_SHORTCUTS}
      />
    </div>
  </div>
) : (
  <Row className="search-field-and-date-filter">
    <Col className="date-filter-column">
      <TimeFilter
        title="Filter By Request Date:"
        changeFilter={handleTimeFilterChange}
        selected={params.timeFilter}
        setDateRange={handleDateRangeChange}
        placeholder="Select Day Or Date Range"
        filterList={['today', 'last 7 days']}
        showClearButton
      />
    </Col>
    <ToggleSwitchesColumn />
  </Row>
)}
            <AppointmentRequestsTable
              stickyTop={tableHeaderPosition}
              heads={headers}
              rows={rows}
              sortField={params.sortField}
              sortOrder={params.sortOrder}
              filters={params.filters}
              handleSort={handleSort}
              handleFilter={handleFilter}
              emptyStateMessage={emptyStateMessage}
            />
            {isDefaultPage && (
              <PaginationComponent
                handlePageLimit={handlePageLimit}
                nextPage={() =>
                  localDispatch({
                    type: 'SET_OFFSET',
                    payload: params.offset + params.limit,
                  })
                }
                prevPage={() =>
                  localDispatch({
                    type: 'SET_OFFSET',
                    payload: params.offset - params.limit,
                  })
                }
                gotoPage={(page) =>
                  localDispatch({
                    type: 'SET_OFFSET',
                    payload: params.limit * page,
                  })
                }
                limit={params.limit}
                offset={params.offset}
                total={total}
              />
            )}
          </Wrapper>
        </Col>
        <MessageModal
          show={showSuccessModal}
          type="ok"
          responseAlertType="danger"
          message="Appointment is successfully created."
          onClose={() => handleToggleSuccessModal('close')}
        />
        <AppointmentRequestModal />
        <AppointmentRequestMessageModal />
      </SuccessModalProvider>
    </AppointmentRequestModalProvider>
  );
}

export default withRouter(AppointmentRequests);
